import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MessageCircle, Plus, SlidersHorizontal, Heart } from "lucide-react";
import Navbar from "../../components/Navbar";
import { mk } from "../../lib/market";
import { CartButton } from "../../components/market/CartButton";
import { EmailGate } from "../../components/market/EmailGate";
import { ListingCard } from "../../components/market/ListingCard";

const KINDS = [["", "Everything"], ["sale", "For sale"], ["rental", "Rentals"], ["free", "Free"]];

export function useMarketMe() {
  const [me, setMe] = useState(undefined);
  const load = useCallback(() => mk.get("/market/me").then((r) => setMe(r.data)).catch(() => setMe(null)), []);
  useEffect(() => { load(); }, [load]);
  return [me, load];
}

function Filters({ f, set, meta }) {
  const sel = "input-luxe !py-2 text-sm";
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="market-filters">
      <select value={f.category} onChange={(e) => set({ category: e.target.value })} className={sel} data-testid="market-filter-category"><option value="">All categories</option>{meta?.categories.map((c) => <option key={c}>{c}</option>)}</select>
      <input value={f.size} onChange={(e) => set({ size: e.target.value })} placeholder="Size" className={sel} data-testid="market-filter-size" />
      <input value={f.location} onChange={(e) => set({ location: e.target.value })} placeholder="City or state" className={sel} data-testid="market-filter-location" />
      <input type="number" min="0" value={f.max_price} onChange={(e) => set({ max_price: e.target.value })} placeholder="Max $" className={sel} data-testid="market-filter-max" />
      <select value={f.seller_type} onChange={(e) => set({ seller_type: e.target.value })} className={sel} data-testid="market-filter-seller"><option value="">Brides & stores</option><option value="bride">Brides</option><option value="store">Bridal stores</option></select>
      <select value={f.sort} onChange={(e) => set({ sort: e.target.value })} className={sel} data-testid="market-filter-sort"><option value="new">Newest</option><option value="price_low">Price: low to high</option><option value="price_high">Price: high to low</option></select>
    </div>
  );
}

function BrowseBanner({ onDone }) {
  const [open, setOpen] = useState(false);
  if (open) return <div className="mb-8"><EmailGate onDone={onDone} /></div>;
  return (
    <div className="mb-8 rounded-2xl border border-[var(--gold)]/40 bg-white px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="market-browse-banner">
      <p className="flex-1 text-sm">Browse freely. You'll only need to confirm your email to save, message a seller or check out.</p>
      <button onClick={() => setOpen(true)} className="btn-outline-gold !py-2 whitespace-nowrap" data-testid="market-browse-confirm">Confirm email</button>
    </div>
  );
}

export default function MarketHome() {
  const [me, reloadMe] = useMarketMe();
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ q: "", kind: "", category: "", size: "", location: "", max_price: "", seller_type: "", sort: "new" });
  const [res, setRes] = useState(null);
  const [more, setMore] = useState(false);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  useEffect(() => { mk.get("/market/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => {
    if (me === undefined) return undefined;
    const t = setTimeout(() => mk.get("/market/listings", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "")) }).then((r) => setRes(r.data)).catch(() => setRes({ items: [] })), 300);
    return () => clearTimeout(t);
  }, [f, me]);
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <Navbar />
      <div className="container-wide px-5 md:px-12 pt-28 pb-20">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-script text-[var(--gold)] text-3xl">Something borrowed</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl">Wedding Consignment</h1>
            <p className="text-[var(--muted)] mt-3 max-w-xl">Gently loved gowns, veils, decor and more from real brides and bridal stores. Buy, rent, or pick up a free donation.</p>
          </div>
          <div className="flex gap-2">
            {me && <Link to="/market/saved" className="btn-outline-gold !py-2.5 whitespace-nowrap" data-testid="market-saved-link"><Heart className="w-4 h-4" /> Saved</Link>}
            {me && <CartButton />}
            {me && <Link to="/market/inbox" className="btn-outline-gold !py-2.5 whitespace-nowrap" data-testid="market-inbox-link"><MessageCircle className="w-4 h-4" /> Messages</Link>}
            <Link to="/market/sell" className="btn-gold !py-2.5 whitespace-nowrap" data-testid="market-sell-link"><Plus className="w-4 h-4" /> Sell</Link>
          </div>
        </div>
        {me === null && <BrowseBanner onDone={reloadMe} />}
        {me !== undefined && (
          <>
            <div className="flex flex-col gap-3 mb-8">
              <div className="flex gap-2">
                <div className="relative flex-1"><Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted)]" /><input value={f.q} onChange={(e) => set({ q: e.target.value })} placeholder="Search gowns, designers, decor…" className="input-luxe !pl-11" data-testid="market-search" /></div>
                <button onClick={() => setMore((m) => !m)} className={`btn-outline-gold !px-4 ${more ? "!bg-[var(--ink)] !text-white" : ""}`} data-testid="market-filters-toggle"><SlidersHorizontal className="w-4 h-4" /></button>
              </div>
              <div className="flex flex-wrap gap-2">{KINDS.map(([k, l]) => <button key={k} onClick={() => set({ kind: k })} className={`px-4 py-1.5 rounded-full text-xs border transition-colors ${f.kind === k ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)] hover:border-[var(--gold)]"}`} data-testid={`market-kind-${k || "all"}`}>{l}</button>)}</div>
              {more && <Filters f={f} set={set} meta={meta} />}
            </div>
            {res && !res.items.length && <p className="font-serif text-3xl text-center text-[var(--muted)] py-20" data-testid="market-empty">Nothing here yet. Try another search, or be the first to list!</p>}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10" data-testid="market-grid">{res?.items.map((l) => <ListingCard key={l.listing_id} l={l} />)}</div>
          </>
        )}
      </div>
    </div>
  );
}
